import React, { useCallback, useEffect, useState } from 'react';
import Icon from '../Icon';

interface NewsGalleryProps {
  images: { src: string; alt: string }[];
}

/**
 * Bildergalerie eines Beitrags: Kachelraster, per Klick als großes Bild
 * über der Seite. Blättern mit den Pfeiltasten, schließen mit Escape.
 */
const NewsGallery: React.FC<NewsGalleryProps> = ({ images }) => {
  const [open, setOpen] = useState<number | null>(null);

  const step = useCallback(
    (delta: number) => setOpen((current) =>
      current === null ? null : (current + delta + images.length) % images.length),
    [images.length],
  );

  useEffect(() => {
    if (open === null) return undefined;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(null);
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    // Solange das große Bild offen ist, soll die Seite dahinter nicht scrollen.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, step]);

  if (images.length === 0) return null;

  const current = open === null ? null : images[open];

  return (
    <>
      <div className="gallery-grid">
        {images.map((image, index) => (
          <button
            key={image.src}
            type="button"
            className="gallery-tile"
            onClick={() => setOpen(index)}
            aria-label={`${image.alt} vergrößern`}
          >
            <img src={image.src} alt={image.alt} loading="lazy" width={1000} height={750} />
          </button>
        ))}
      </div>

      {current && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={current.alt}
          onClick={() => setOpen(null)}
        >
          <button type="button" className="lightbox__close" aria-label="Schließen">
            <Icon name="x" size={22} />
          </button>
          {images.length > 1 && (
            <button
              type="button"
              className="lightbox__nav lightbox__nav--prev"
              aria-label="Vorheriges Bild"
              onClick={(event) => {
                event.stopPropagation();
                step(-1);
              }}
            >
              <Icon name="chevron-left" size={26} />
            </button>
          )}
          <figure className="lightbox__figure" onClick={(event) => event.stopPropagation()}>
            <img src={current.src} alt={current.alt} />
            <figcaption>
              {open! + 1} von {images.length}
            </figcaption>
          </figure>
          {images.length > 1 && (
            <button
              type="button"
              className="lightbox__nav lightbox__nav--next"
              aria-label="Nächstes Bild"
              onClick={(event) => {
                event.stopPropagation();
                step(1);
              }}
            >
              <Icon name="chevron-right" size={26} />
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default NewsGallery;
